import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Edit, Trash2, RefreshCw, Shield, Plus, ArrowLeft, MousePointerClick, ShoppingBag, Star, Tag, Users } from 'lucide-react';
import { getGigById, softDeleteGig, restoreGig, toggleActive, duplicateGig } from '../../services/gig.service';
import { format } from 'date-fns';
import { userimg } from '../../assets/assets';

const StatCard = ({ icon: Icon, label, value, color = 'text-[#174568]' }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-3 shadow-sm">
    <div className={`p-2 rounded-lg bg-gray-50 ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-gray-800">{value ?? '—'}</p>
    </div>
  </div>
);

const AdminGigDetail = () => {
  const { gigId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminGigDetail', gigId],
    queryFn: () => getGigById(gigId),
  });

  const gig = data?.gig;
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['adminGigDetail', gigId] }); queryClient.invalidateQueries({ queryKey: ['adminGigs'] }); };

  const softDelete = useMutation({ mutationFn: () => softDeleteGig(gigId), onSuccess: () => { toast.success('Moved to trash'); invalidate(); navigate('/admins/gigs'); } });
  const restore    = useMutation({ mutationFn: () => restoreGig(gigId),   onSuccess: () => { toast.success('Gig restored'); invalidate(); } });
  const toggle     = useMutation({ mutationFn: () => toggleActive(gigId), onSuccess: (d) => { toast.success(d.message); invalidate(); } });
  const dup        = useMutation({ mutationFn: () => duplicateGig(gigId), onSuccess: (d) => { toast.success('Duplicated as draft'); navigate(`/admins/edit-gig/${d.gig._id}`); } });

  if (isLoading) return <div className="p-12 text-center text-gray-400">Loading…</div>;
  if (isError || !gig) return <div className="p-12 text-center text-red-400">Gig not found.</div>;

  const avgRating = gig.starNumber > 0 ? (gig.totalStars / gig.starNumber).toFixed(1) : '0.0';

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 font-['Poppins']">
      {/* Back */}
      <button onClick={() => navigate('/admins/gigs')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-5">
        <ArrowLeft size={16} /> Back to Gigs
      </button>

      {/* Top bar */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{gig.title}</h1>
          <p className="text-xs text-gray-400 mt-1">
            Created {gig.createdAt ? format(new Date(gig.createdAt), 'd MMM yyyy') : '—'}
            {gig.deletedAt && <span className="ml-2 text-red-400">• Deleted {format(new Date(gig.deletedAt), 'd MMM yyyy')}</span>}
          </p>
          <div className="flex gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${gig.isActive ? 'border-green-500 text-green-600' : 'border-gray-400 text-gray-500'}`}>
              {gig.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${gig.status === 'published' ? 'border-blue-400 text-blue-500' : 'border-yellow-400 text-yellow-600'}`}>
              {gig.status}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {!gig.deletedAt ? (
            <>
              <button onClick={() => navigate(`/admins/edit-gig/${gig._id}`)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-[#174568] text-[#174568] rounded-lg hover:bg-[#174568] hover:text-white transition-colors">
                <Edit size={14} /> Edit
              </button>
              <button onClick={() => dup.mutate()} disabled={dup.isPending} className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                <Plus size={14} /> Duplicate
              </button>
              <button onClick={() => toggle.mutate()} disabled={toggle.isPending} className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                <Shield size={14} /> {gig.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => softDelete.mutate()} disabled={softDelete.isPending} className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-red-400 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 size={14} /> Delete
              </button>
            </>
          ) : (
            <button onClick={() => restore.mutate()} disabled={restore.isPending} className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <RefreshCw size={14} /> Restore from Trash
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={MousePointerClick} label="Clicks"  value={Math.floor(gig.clicks ?? 0)} />
        <StatCard icon={ShoppingBag}       label="Orders"  value={gig.sales ?? 0} />
        <StatCard icon={Star}              label="Rating"  value={avgRating} color="text-yellow-500" />
        <StatCard icon={Users}             label="Reviews" value={gig.starNumber ?? 0} color="text-purple-500" />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Left: Cover + hellocians + creator */}
        <div className="space-y-4">
          {/* Cover */}
          {gig.cover && (
            <img src={gig.cover} alt={gig.title} className="w-full h-48 object-cover rounded-xl border border-gray-100 shadow-sm" />
          )}

          {/* Creator */}
          {gig.creator && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <p className="text-xs text-gray-400 uppercase mb-2">Created by</p>
              <div className="flex items-center gap-2">
                <img src={gig.creator.profilePicture || userimg} alt="creator" className="w-9 h-9 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold">{gig.creator.firstName} {gig.creator.lastName}</p>
                  {gig.creator.username && <p className="text-xs text-blue-400">@{gig.creator.username}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Hellocians */}
          {gig.hellocians && gig.hellocians.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <p className="text-xs text-gray-400 uppercase mb-2">Hellocians</p>
              <div className="space-y-2">
                {gig.hellocians.map((h) => (
                  <div key={h._id} className="flex items-center gap-2">
                    <img src={h.profilePicture || userimg} alt={h.firstName} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium">{h.firstName} {h.lastName}</p>
                      <p className="text-xs text-blue-400">@{h.username || h._id}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {gig.tags && gig.tags.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <p className="text-xs text-gray-400 uppercase mb-2 flex items-center gap-1"><Tag size={12} /> Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {gig.tags.map((t) => (
                  <span key={t._id || t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">#{t.name || t}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Description + pricing */}
        <div className="md:col-span-2 space-y-4">
          {/* Description */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Description</h2>
            <div
              className="text-sm text-gray-600 leading-relaxed prose max-w-none"
              dangerouslySetInnerHTML={{ __html: gig.description || '<em>No description</em>' }}
            />
          </div>

          {/* Pricing packages */}
          {gig.pricing && (
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Pricing Packages</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {['basic','standard','advanced','pro','premium'].map((pkg) => {
                  const p = gig.pricing[pkg];
                  if (!p || !p.title) return null;
                  return (
                    <div key={pkg} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold uppercase text-[#174568]">{pkg}</span>
                        <span className="text-sm font-bold">${p.price}</span>
                      </div>
                      <p className="text-xs font-medium text-gray-700 mb-0.5">{p.title}</p>
                      <p className="text-xs text-gray-400">{p.deliveryTimeframe}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGigDetail;
